// index.jsx
import React, { useState, useEffect } from 'react';
import { employeeAPI } from '../actions';
import { toast } from 'react-toastify';
import PhoneInput from '../../../../../cabinet/wizplain/widgets/phone';
import EmployeePhotoBlock from './EmployeePhotoBlock';
import EmployeePublicProfile from './EmployeePublicProfile';
import { validateEmployeeForm, showValidationErrors } from './validation';
import './EmployeeModal.css';

const EmployeeModal = ({ 
  isOpen, 
  onClose, 
  employeeData, 
  onSave, 
  onDismiss, 
  onRestore,
  availablePositions = [],
  kindergartenId 
}) => {
  const [employee, setEmployee] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    rnokpp: '',
    birthDate: '',
    gender: 'Жіноча',
    education: 'Спеціаліст',
    marks: {
      maternityLeave: false,
      idle: false,
      mobilized: false,
      contractTerminated: false,
      after3year: false, 
      before3year: false, 
      unload: false, 
      hiOOP: false
    },
    experience: {
      general: '',
      pedagogical: '',
      category: 'Спеціаліст',
      firstWorkDay: ''
    },
    appointment: {
      number: '',
      date: ''
    },
    contacts: [
      { type: 'Телефон', value: '' }
    ],
    attestation: {
      diplomaDate: '',
      lastAttestation: '',
      nextAttestation: '',
      unscheduled: false,
      inPlan: false
    },
    departments: [
      {
        staffPositionId: '',
        positionName: '',
        positionType: 'pedagogical',
        promotion: false,
        unit: 0.5,
        combination: false,
        department: '',
        selected: true
      }
    ]
  });

  const [avStaffCount, setAvStaffCount] = useState([]);
  const [loading, setLoading] = useState(false);
  const [positionDropdownVisible, setPositionDropdownVisible] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false); // Новое состояние для режима редактирования

  // Обновление состояния при изменении employeeData
  useEffect(() => {
    if (employeeData) {
      // Преобразуем данные из API в формат формы
      const formattedData = {
        ...employeeData,
        id:        employeeData._id || employeeData.id,
        photos:    [],
        photoDocId: employeeData.photoDocId || null,
        publicProfile: {
          isPublic:       false,
          displayOrder:   999,
          displayGroup:   3,
          publicPosition: '',
          bio:            '',
          avatarFileId:   null,
          ...employeeData.publicProfile,
        },
        birthDate: employeeData.birthDate ? formatDateForInput(employeeData.birthDate) : '',
        experience: {
          ...employeeData.experience,
          firstWorkDay: employeeData.experience?.firstWorkDay ? formatDateForInput(employeeData.experience.firstWorkDay) : ''
        },
        appointment: {
          ...employeeData.appointment,
          date: employeeData.appointment?.date ? formatDateForInput(employeeData.appointment.date) : ''
        },
        attestation: {
          ...employeeData.attestation,
          diplomaDate: employeeData.attestation?.diplomaDate ? formatDateForInput(employeeData.attestation.diplomaDate) : '',
          lastAttestation: employeeData.attestation?.lastAttestation ? formatDateForInput(employeeData.attestation.lastAttestation) : '',
          nextAttestation: employeeData.attestation?.nextAttestation ? formatDateForInput(employeeData.attestation.nextAttestation) : ''
        },
        departments: employeeData.departments?.map(dept => ({
          staffPositionId: dept.staffPositionId?._id || dept.staffPositionId,
          positionName: dept.positionName,
          positionType: dept.positionType,
          promotion: dept.promotion || false,
          unit: parseFloat(dept.unit) || 0.5,
          combination: dept.combination || false,
          department: dept.department || '',
          selected: dept.selected || false
        })) || []
      };
      
      setEmployee(formattedData);
      setPositionDropdownVisible(new Array(formattedData.departments.length).fill(false));

      // Завантажуємо фото для існуючого співробітника
      if (employeeData._id || employeeData.id) {
        const empId = employeeData._id || employeeData.id;
        employeeAPI.getPhotos(kindergartenId, empId)
          .then(function(result) {
            setEmployee(prev => ({
              ...prev,
              photos:    result.photos    || [],
              photoDocId: result.docId   || prev.photoDocId,
              publicProfile: {
                ...prev.publicProfile,
                avatarFileId: result.avatarFileId || prev.publicProfile?.avatarFileId || null,
              }
            }));
          })
          .catch(function(e) { console.error('getPhotos error:', e); });
      }
      
      // Для существующего сотрудника по умолчанию режим просмотра
      setIsEditing(false);
    } else {
      // Сброс до начального состояния для нового сотрудника
      setEmployee({
        lastName: '',
        firstName: '',
        middleName: '',
        rnokpp: '',
        birthDate: '',
        gender: 'Жіноча',
        education: 'Спеціаліст',
        marks: {
          maternityLeave: false,
          idle: false,
          mobilized: false,
          contractTerminated: false,
          after3year: false, 
          before3year: false, 
          unload: false, 
          hiOOP: false
        },
        experience: {
          general: '',
          pedagogical: '',
          category: 'Спеціаліст',
          firstWorkDay: ''
        },
        appointment: {
          number: '',
          date: ''
        },
        contacts: [
          { type: 'Телефон', value: '' }
        ],
        attestation: {
          diplomaDate: '',
          lastAttestation: '',
          nextAttestation: '',
          unscheduled: false,
          inPlan: false
        },
        departments: [
          {
            staffPositionId: '',
            positionName: '',
            positionType: 'pedagogical',
            promotion: false,
            unit: 0.5,
            combination: false,
            department: '',
            selected: true
          }
        ]
      });
      setPositionDropdownVisible([false]);
      
      // Для нового сотрудника по умолчанию режим редактирования
      setIsEditing(true);
    }
    // Сбрасываем ошибки валидации при изменении данных
    setValidationErrors({});
  }, [employeeData]);

  // Форматирование даты для input[type="date"]
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Форматирование даты для отправки на сервер
  const formatDateForServer = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toISOString();
  };

  const handleMarkChange = (mark, value) => {
    setEmployee(prev => ({
      ...prev,
      marks: {
        ...prev.marks,
        [mark]: value
      }
    }));
  };

  const handleAttestationChange = (field, value) => {
    setEmployee(prev => ({
      ...prev,
      attestation: {
        ...prev.attestation,
        [field]: value
      }
    }));
  };

  const handleDepartmentChange = (index, field, value) => {
    if (field === 'staffPositionId') {
      console.log('handleDepartmentChange staffPositionId value:', value, typeof value);
      console.log('availablePositions sample:', availablePositions[0]);
    }
    const updatedDepartments = [...employee.departments];
    updatedDepartments[index] = {
      ...updatedDepartments[index],
      [field]: value
    };
    
    // Если выбрана штатная позиция, обновляем название и тип
    if (field === 'staffPositionId' && value) {
      const selectedPosition = availablePositions.find(pos => pos.id === value);
      if (selectedPosition) {
        updatedDepartments[index].positionName = selectedPosition.positionName;
        updatedDepartments[index].positionType = selectedPosition.positionType;
        setAvStaffCount(getCountStaffOpt(selectedPosition.availableUnits));
      }
    }
    
    setEmployee(prev => ({ ...prev, departments: updatedDepartments }));
    // Очищаем ошибки валидации при изменении
    if (validationErrors.departments?.[index]?.[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors.departments[index][field];
      if (Object.keys(newErrors.departments[index]).length === 0) {
        delete newErrors.departments[index];
      }
      if (Object.keys(newErrors.departments).length === 0) {
        delete newErrors.departments;
      }
      setValidationErrors(newErrors);
    }
  };

  // Функции для редактирования контактов
  const handleContactTypeChange = (index, value) => {
    const updatedContacts = [...employee.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      type: value
    };
    setEmployee(prev => ({ ...prev, contacts: updatedContacts }));
  };

  const handleContactValueChange = (index, value) => {
    const updatedContacts = [...employee.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      value: value
    };
    setEmployee(prev => ({ ...prev, contacts: updatedContacts }));
    // Очищаем ошибки валидации при изменении
    if (validationErrors.contacts?.[index]?.value) {
      const newErrors = { ...validationErrors };
      delete newErrors.contacts[index].value;
      if (Object.keys(newErrors.contacts[index]).length === 0) {
        delete newErrors.contacts[index];
      }
      if (Object.keys(newErrors.contacts).length === 0) {
        delete newErrors.contacts;
      }
      setValidationErrors(newErrors);
    }
  };

  const deleteContact = (index) => {
    const updatedContacts = employee.contacts.filter((_, i) => i !== index);
    setEmployee(prev => ({ ...prev, contacts: updatedContacts }));
    // Очищаем ошибки валидации при удалении
    if (validationErrors.contacts?.[index]) {
      const newErrors = { ...validationErrors };
      delete newErrors.contacts[index];
      if (Object.keys(newErrors.contacts).length === 0) {
        delete newErrors.contacts;
      }
      setValidationErrors(newErrors);
    }
  };

  const moveDepartment = (index, direction) => {
    const updatedDepartments = [...employee.departments];
    if (direction === 'up' && index > 0) {
      [updatedDepartments[index], updatedDepartments[index - 1]] = 
      [updatedDepartments[index - 1], updatedDepartments[index]];
    } else if (direction === 'down' && index < updatedDepartments.length - 1) {
      [updatedDepartments[index], updatedDepartments[index + 1]] = 
      [updatedDepartments[index + 1], updatedDepartments[index]];
    }
    setEmployee(prev => ({ ...prev, departments: updatedDepartments }));
  };

  const deleteDepartment = (index) => {
    const updatedDepartments = employee.departments.filter((_, i) => i !== index);
    setEmployee(prev => ({ ...prev, departments: updatedDepartments }));
    setPositionDropdownVisible(prev => prev.filter((_, i) => i !== index));
    // Очищаем ошибки валидации при удалении
    if (validationErrors.departments?.[index]) {
      const newErrors = { ...validationErrors };
      delete newErrors.departments[index];
      if (Object.keys(newErrors.departments).length === 0) {
        delete newErrors.departments;
      }
      setValidationErrors(newErrors);
    }
  };

  const addContact = () => {
    const updatedContacts = [...employee.contacts, { type: 'Телефон', value: '' }];
    setEmployee(prev => ({ ...prev, contacts: updatedContacts }));
  };

  const addDepartment = () => {
    const newDepartment = {
      staffPositionId: '',
      positionName: '',
      positionType: 'pedagogical',
      promotion: false,
      unit: 0.5,
      combination: false,
      department: '',
      selected: false
    };
    setEmployee(prev => ({
      ...prev,
      departments: [...prev.departments, newDepartment]
    }));
    setPositionDropdownVisible(prev => [...prev, false]);
  };

  // Функции для работы с выбором должности
  const togglePositionDropdown = (index) => {
    setPositionDropdownVisible(prev => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const handlePositionSelect = (index, position) => {
    handleDepartmentChange(index, 'staffPositionId', position.id);
    setPositionDropdownVisible(prev => {
      const newState = [...prev];
      newState[index] = false;
      return newState;
    });
  };

  // Проверка доступности штатных единиц
  const checkPositionAvailability = async (staffPositionId, requiredUnits, employeeId) => {
    try {
      const availability = await employeeAPI.checkPositionAvailability(
        kindergartenId,
        { staffPositionId, requiredUnits, employeeId }
      );
      return availability;
    } catch (error) {
      console.error('Ошибка проверки доступности:', error);
      return { isValid: false, message: 'Помилка перевірки доступності' };
    }
  };

  const handleClose = () => {
    // Очищаем ошибки при закрытии
    setValidationErrors({});
    onClose();
  };

  const handleSave = async () => {
    // Валидация формы
    const validation = validateEmployeeForm(employee);
    setValidationErrors(validation.errors);

    console.log('personal save, data: ', employee);
    console.log('personal save, validation: ', validation);
    
    if (!validation.isValid) {
      showValidationErrors(validation.errors, toast);
      return;
    }

    setLoading(true);
    try {
      // Подготавливаем данные для отправки
      const employeeToSave = {
        ...employee,
        fullName: [employee.firstName, employee.middleName, employee.lastName].join(" "),
        birthDate: formatDateForServer(employee.birthDate),
        experience: {
          ...employee.experience,
          firstWorkDay: formatDateForServer(employee.experience.firstWorkDay)
        },
        appointment: {
          ...employee.appointment,
          date: formatDateForServer(employee.appointment.date)
        },
        attestation: {
          ...employee.attestation,
          diplomaDate: formatDateForServer(employee.attestation.diplomaDate),
          lastAttestation: formatDateForServer(employee.attestation.lastAttestation),
          nextAttestation: formatDateForServer(employee.attestation.nextAttestation)
        },
        departments: employee.departments.map(dept => ({
          ...dept,
          unit: parseFloat(dept.unit)
        }))
      };

      // Проверяем доступность всех должностей
      for (const dept of employeeToSave.departments) {
        if (dept.staffPositionId) {
          const availability = await checkPositionAvailability(dept.staffPositionId, dept.unit, employee._id);
          if (!availability.isValid) {
            //toast.error(`Помилка для посади "${dept.positionName}": ${availability.message}`);
            alert(`Помилка для посади "${dept.positionName}": ${availability.message}`)
            setLoading(false);
            return;
          }
        }
      }

      if (onSave) {
        await onSave(employeeToSave);

        // Зберігаємо публічний профіль окремо
        if (employee._id && employee.publicProfile) {
          console.log('bio length:', employee.publicProfile.bio && employee.publicProfile.bio.length);
          console.log('bio:', employee.publicProfile.bio);
          employeeAPI.updatePublicProfile(kindergartenId, employee._id, {
            isPublic:       employee.publicProfile.isPublic,
            publicPosition: employee.publicProfile.publicPosition,
            bio:            employee.publicProfile.bio,
            displayOrder:   employee.publicProfile.displayOrder,
          }).catch(function(e) { console.error('updatePublicProfile error:', e); });
        }

        setValidationErrors({});
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      //toast.error('Помилка при збереженні даних');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (employee._id && onDismiss) {
      setLoading(true);
      try {
        await onDismiss(employee._id);
      } catch (error) {
        console.error('Ошибка увольнения:', error);
        //toast.error('Помилка при звільненні працівника');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestore = async () => {
    if (employee._id && onRestore) {
      setLoading(true);
      try {
        await onRestore(employee._id);
      } catch (error) {
        console.error('Ошибка восстановления:', error);
        //toast.error('Помилка при поновленні працівника');
      } finally {
        setLoading(false);
      }
    }
  };

  // Функции для управления режимом редактирования
  const handleEditToggle = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Если это новый сотрудник, закрываем модальное окно
    if (!employeeData) {
      onClose();
    } else {
      // Если это существующий сотрудник, возвращаемся к исходным данным
      const formattedData = {
        ...employeeData,
        birthDate: employeeData.birthDate ? formatDateForInput(employeeData.birthDate) : '',
        experience: {
          ...employeeData.experience,
          firstWorkDay: employeeData.experience?.firstWorkDay ? formatDateForInput(employeeData.experience.firstWorkDay) : ''
        },
        appointment: {
          ...employeeData.appointment,
          date: employeeData.appointment?.date ? formatDateForInput(employeeData.appointment.date) : ''
        },
        attestation: {
          ...employeeData.attestation,
          diplomaDate: employeeData.attestation?.diplomaDate ? formatDateForInput(employeeData.attestation.diplomaDate) : '',
          lastAttestation: employeeData.attestation?.lastAttestation ? formatDateForInput(employeeData.attestation.lastAttestation) : '',
          nextAttestation: employeeData.attestation?.nextAttestation ? formatDateForInput(employeeData.attestation.nextAttestation) : ''
        },
        departments: employeeData.departments?.map(dept => ({
          staffPositionId: dept.staffPositionId?._id || dept.staffPositionId,
          positionName: dept.positionName,
          positionType: dept.positionType,
          promotion: dept.promotion || false,
          unit: parseFloat(dept.unit) || 0.5,
          combination: dept.combination || false,
          department: dept.department || '',
          selected: dept.selected || false
        })) || []
      };
      
      setEmployee(formattedData);
      setIsEditing(false);
    }
    setValidationErrors({});
  };

  const formatRnokpp = (val)=>{
    const val_match = val.match(/[0-9]/g, '');
    return val_match && val_match.join("").slice(0,10) || '';
  }

  // Функция для получения класса ошибки
  const getFieldErrorClass = (fieldName, index = null) => {
    if (index !== null) {
      if (validationErrors[fieldName]?.[index]) {
        return 'is-invalid';
      }
    } else {
      if (validationErrors[fieldName]) {
        return 'is-invalid';
      }
    }
    return '';
  };

  // Функция для рендеринга поля ввода или отображения значения
  const renderField = (field, value, onChange, type = 'text', options = {}) => {
    // error виймаємо з options, щоб він не потрапив атрибутом у DOM
    const { error, ...inputOptions } = options;
    const errorClass = `${getFieldErrorClass(field)} ${error ? 'is-invalid' : ''}`.trim();
    const feedback = error ? <div className="invalid-feedback d-block">{error}</div> : null;

    if (isEditing) {
      switch (type) {
        case 'text':
          return (
            <>
              <input
                type="text"
                className={`form-control ${errorClass}`}
                value={value}
                onChange={onChange}
                disabled={loading}
                {...inputOptions}
              />
              {feedback}
            </>
          );
        case 'date':
          return (
            <>
              <input
                type="date"
                className={`form-control ${errorClass}`}
                value={value}
                onChange={onChange}
                disabled={loading}
                {...inputOptions}
              />
              {feedback}
            </>
          );
        case 'select':
          return (
            <>
              <select
                className={`form-select ${errorClass}`}
                value={value}
                onChange={onChange}
                disabled={loading}
                {...inputOptions}
              >
                {inputOptions.children}
              </select>
              {feedback}
            </>
          );
        case 'checkbox':
          return (
            <input
              className="form-check-input"
              type="checkbox"
              checked={value}
              onChange={onChange}
              disabled={loading}
              {...inputOptions}
            />
          );
        default:
          return null;
      }
    } else {
      // Режим просмотра - отображаем значение
      if (type === 'checkbox') {
        return(
          <input
              className="form-check-input"
              type="checkbox"
              checked={value}
              onChange={onChange}
              disabled={true}
              {...options}
            />
        ) 
//        <div className="text-value">{value ? 'Так' : 'Ні'}</div>;
      } else if (type === 'select') {
        const selectedOption = options.children?.find(opt => opt.props.value === value);
        return <div className="text-value">{selectedOption ? selectedOption.props.children : value}</div>;
      } else {
        return <div className="text-value">{value || 'не вказано'}</div>;
      }
    }
  };

  if (!isOpen) return null;

  const getCountStaffOpt = (units)=>{
    const out = [];

    for(let i=0; i <= units; i = Math.round((i + 0.01) * 100) / 100 ){
      out.push(i);
    }

    return out;
  
  }


  return (
    <div className="modal fade employee-modal show" style={{ display: 'block' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {employeeData ? 'Картка працівника' : 'Створення нового працівника'}
            </h5>
            <div className="d-flex align-items-center gap-2">
              {/* Кнопка редактирования для существующих сотрудников в режиме просмотра */}
              {employeeData && !isEditing && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleEditToggle}
                  disabled={loading}
                >
                  Редагувати
                </button>
              )}
              {/* Кнопка отмены редактирования */}
              {isEditing && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  Скасувати
                </button>
              )}
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={loading}
              ></button>
            </div>
          </div>
          
          <div className="modal-body">
            {loading && (
              <div className="loading-overlay">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Завантаження...</span>
                </div>
              </div>
            )}

            {employeeData && (
              <EmployeePhotoBlock
                employee={employee}
                employeeId={employee._id}
                kindergartenId={kindergartenId}
                isEditing={isEditing}
                onAvatarChange={(fileId) => setEmployee(prev => ({
                  ...prev,
                  publicProfile: { ...prev.publicProfile, avatarFileId: fileId }
                }))}
              />
            )}


            {/* Дані особи */}
            <div className="section section--after-divider">
              <h6>Дані особи</h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Прізвище *</label>
                  {renderField('lastName', employee.lastName, (e) => {
                    setEmployee({...employee, lastName: e.target.value});
                    if (validationErrors.lastName) {
                      setValidationErrors(prev => ({...prev, lastName: undefined}));
                    }
                  }, 'text', { required: true, error: validationErrors.lastName })}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Ім'я *</label>
                  {renderField('firstName', employee.firstName, (e) => {
                    setEmployee({...employee, firstName: e.target.value});
                    if (validationErrors.firstName) {
                      setValidationErrors(prev => ({...prev, firstName: undefined}));
                    }
                  }, 'text', { required: true, error: validationErrors.firstName })}
                </div>
                <div className="col-md-4">
                  <label className="form-label">По батькові</label>
                  {renderField('middleName', employee.middleName, (e) => setEmployee({...employee, middleName: e.target.value}), 'text')}
                </div>
                <div className="col-md-3">
                  <label className="form-label">РНОКПП</label>
                  {renderField('rnokpp', employee.rnokpp, (e) => {
                    setEmployee({...employee, rnokpp: formatRnokpp(e.target.value)});
                    if (validationErrors.rnokpp) {
                      setValidationErrors(prev => ({...prev, rnokpp: undefined}));
                    }
                  }, 'text', { placeholder: "3495678801", error: validationErrors.rnokpp })}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Дата народження</label>
                  {renderField('birthDate', employee.birthDate, (e) => {
                    setEmployee({...employee, birthDate: e.target.value});
                    if (validationErrors.birthDate) {
                      setValidationErrors(prev => ({...prev, birthDate: undefined}));
                    }
                  }, 'date', { error: validationErrors.birthDate })}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Стать</label>
                  {renderField('gender', employee.gender, (e) => setEmployee({...employee, gender: e.target.value}), 'select', {
                    children: [
                      <option key="female" value="Жіноча">Жіноча</option>,
                      <option key="male" value="Чоловіча">Чоловіча</option>
                    ]
                  })}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Освіта</label>
                  {renderField('education', employee.education, (e) => setEmployee({...employee, education: e.target.value}), 'select', {
                    children: [
                      <option value="Немає">Немає</option>,
                      <option value="Спеціаліст">Спеціаліст</option>,
                      <option value="Бакалавр">Бакалавр</option>,
                      <option value="Магістр">Магістр</option>
                    ]
                  })}
                </div>
              </div>
            </div>

            {/* Особливі відмітки */}
            <div className="section section--marks section--after-divider">
              <h6>Особливі відмітки працівника</h6>
              <div className="checks-grid">
                <div className="form-check">
                  {renderField('maternityLeave', employee.marks.maternityLeave, 
                    (e) => handleMarkChange('maternityLeave', e.target.checked), 'checkbox', { id: "m1" }
                  )}
                  <label className="form-check-label" htmlFor="m1">
                    В декретній відпустці
                  </label>
                </div>
                <div className="form-check">
                  {renderField('idle', employee.marks.idle, 
                    (e) => handleMarkChange('idle', e.target.checked), 'checkbox', { id: "m2" }
                  )}
                  <label className="form-check-label" htmlFor="m2">
                    На простої
                  </label>
                </div>
                <div className="form-check">
                  {renderField('mobilized', employee.marks.mobilized, 
                    (e) => handleMarkChange('mobilized', e.target.checked), 'checkbox', { id: "m3" }
                  )}
                  <label className="form-check-label" htmlFor="m3">
                    Мобілізований
                  </label>
                </div>
                <div className="form-check">
                  {renderField('contractTerminated', employee.marks.contractTerminated, 
                    (e) => handleMarkChange('contractTerminated', e.target.checked), 'checkbox', { id: "m4" }
                  )}
                  <label className="form-check-label" htmlFor="m4">
                    Припинено трудовий договір
                  </label>
                </div>
                <div className="form-check">
                  {renderField('after3year', employee.marks.after3year, 
                    (e) => handleMarkChange('after3year', e.target.checked), 'checkbox', { id: "m4" }
                  )}
                  <label className="form-check-label" htmlFor="m4">
                    працює з дітьми старше 3 років
                  </label>
                </div>
                <div className="form-check">
                  {renderField('before3year', employee.marks.before3year, 
                    (e) => handleMarkChange('before3year', e.target.checked), 'checkbox', { id: "m4" }
                  )}
                  <label className="form-check-label" htmlFor="m4">
                    працює з дітьми до 3 років
                  </label>
                </div>
                <div className="form-check">
                  {renderField('unload', employee.marks.unload, 
                    (e) => handleMarkChange('unload', e.target.checked), 'checkbox', { id: "m4" }
                  )}
                  <label className="form-check-label" htmlFor="m4">
                    має педагогічне навантаження менше норми
                  </label>
                </div>
                <div className="form-check">
                  {renderField('hiOOP', employee.marks.hiOOP, 
                    (e) => handleMarkChange('hiOOP', e.target.checked), 'checkbox', { id: "m4" }
                  )}
                  <label className="form-check-label" htmlFor="m4">
                    підвищили кваліфікацію з підтримки дітей з ООП
                  </label>
                </div>
                
              </div>
            </div>

            

            {/*В картці працівника додати графи (працює з дітьми до 3 років, старше 3 років, має педагогічне навантаження менше норми, підвищили кваліфікацію з підтримки дітей з ООП)*/}

            {/* Кваліфікація */}
            <div className="section">
              <h6>Кваліфікація</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Стаж загальний</label>
                  {renderField('general', employee.experience.general, 
                    (e) => setEmployee({...employee, experience: {...employee.experience, general: e.target.value}}), 'text'
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Стаж педагогічний</label>
                  {renderField('pedagogical', employee.experience.pedagogical, 
                    (e) => setEmployee({...employee, experience: {...employee.experience, pedagogical: e.target.value}}), 'text'
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Кваліфікаційна категорія</label>
                  {renderField('category', employee.experience.category, 
                    (e) => setEmployee({...employee, experience: {...employee.experience, category: e.target.value}}), 'select', {
                      children: [
                        <option value="Немає">Немає</option>,
                        <option value="Спеціаліст">Спеціаліст</option>,
                        <option value="Перша категорія">Перша категорія</option>,
                        <option value="Друга категорія">Друга категорія</option>,
                        <option value="Вища категорія">Вища категорія</option>
                      ]
                    }
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Дата першого дня роботи</label>
                  {renderField('firstWorkDay', employee.experience.firstWorkDay,
                    (e) => {
                      setEmployee({...employee, experience: {...employee.experience, firstWorkDay: e.target.value}});
                      if (validationErrors.experience?.firstWorkDay) {
                        setValidationErrors(prev => ({...prev, experience: {...prev.experience, firstWorkDay: undefined}}));
                      }
                    }, 'date', { error: validationErrors.experience?.firstWorkDay }
                  )}
                </div>
              </div>
            </div>

            {/* Наказ про призначення */}
            <div className="section section--after-divider">
              <h6>Наказ про призначення</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Номер наказу</label>
                  {renderField('number', employee.appointment.number, 
                    (e) => setEmployee({...employee, appointment: {...employee.appointment, number: e.target.value}}), 'text'
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Дата наказу</label>
                  {renderField('date', employee.appointment.date,
                    (e) => {
                      setEmployee({...employee, appointment: {...employee.appointment, date: e.target.value}});
                      if (validationErrors.appointment?.date) {
                        setValidationErrors(prev => ({...prev, appointment: {...prev.appointment, date: undefined}}));
                      }
                    }, 'date', { error: validationErrors.appointment?.date }
                  )}
                </div>
              </div>
            </div>

            {/* Контактні дані */}
            <div className="section">
              <h6>Контактні дані</h6>
              <div className="bordered-block contact-list">
                <div className="table-bordered-wrap">
                  {employee.contacts.map((contact, index) => (
                    <div key={index} className="contact-row">
                      <div className="contact-type">
                        {isEditing ? (
                          <select
                            className={`form-select contact-type-select ${getFieldErrorClass('contacts', index)}`}
                            value={contact.type}
                            onChange={(e) => handleContactTypeChange(index, e.target.value)}
                            disabled={loading}
                          >
                            <option value="Телефон">Телефон</option>
                            <option value="Email">Email</option>
                          </select>
                        ) : (
                          <div className="text-value">{contact.type}</div>
                        )}
                      </div>
                      <div className="contact-value" >
                        {isEditing ? (
                          <>
                            {contact.type === 'Телефон' && (
                              <PhoneInput 
                                value={contact.value} 
                                onChange={(e) => handleContactValueChange(index, e.target.value)}
                                disabled={loading}
                                error={validationErrors['contacts']?.[index]?.value}
                                isErrorText={false}
                              />
                            )}
                            {contact.type === 'Email' && (
                              <input
                                type="email"
                                className={`form-control contact-value-input ${getFieldErrorClass('contacts', index)}`}
                                value={contact.value}
                                onChange={(e) => handleContactValueChange(index, e.target.value)}
                                placeholder="Введіть email"
                                disabled={loading}
                              />
                            )}
                          </>
                        ) : (
                          <div className="text-value">{contact.value || '-'}</div>
                        )}
                      </div>
                      <div className="contact-actions">
                        {isEditing && employee.contacts.length > 1 && (
                          <button
                            className="contact-delete-btn"
                            onClick={() => deleteContact(index)}
                            disabled={loading}
                            type="button"
                          >
                            <span>🗑️</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {isEditing && (
                  <div className="contact-actions">
                    <a
                      className="add-contact-link"
                      onClick={addContact}
                      disabled={loading}
                    >
                      Додати контакт
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Атестація */}
            <div className="section">
              <h6>Атестація</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Дата отримання диплому</label>
                  {renderField('diplomaDate', employee.attestation.diplomaDate,
                    (e) => {
                      handleAttestationChange('diplomaDate', e.target.value);
                      if (validationErrors.attestation?.diplomaDate) {
                        setValidationErrors(prev => ({...prev, attestation: {...prev.attestation, diplomaDate: undefined}}));
                      }
                    }, 'date', { error: validationErrors.attestation?.diplomaDate }
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Дата останньої атестації</label>
                  {renderField('lastAttestation', employee.attestation.lastAttestation, 
                    (e) => handleAttestationChange('lastAttestation', e.target.value), 'date'
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Дата наступної атестації</label>
                  {renderField('nextAttestation', employee.attestation.nextAttestation,
                    (e) => {
                      handleAttestationChange('nextAttestation', e.target.value);
                      if (validationErrors.attestation?.nextAttestation) {
                        setValidationErrors(prev => ({...prev, attestation: {...prev.attestation, nextAttestation: undefined}}));
                      }
                    }, 'date', { error: validationErrors.attestation?.nextAttestation }
                  )}
                </div>
              </div>
              <div className="checks-grid mt-3">
                <div className="form-check">
                  {renderField('unscheduled', employee.attestation.unscheduled, 
                    (e) => handleAttestationChange('unscheduled', e.target.checked), 'checkbox', { id: "a1" }
                  )}
                  <label className="form-check-label" htmlFor="a1">
                    Позапланова
                  </label>
                </div>
                <div className="form-check">
                  {renderField('inPlan', employee.attestation.inPlan, 
                    (e) => handleAttestationChange('inPlan', e.target.checked), 'checkbox', { id: "a2" }
                  )}
                  <label className="form-check-label" htmlFor="a2">
                    Чи входить до плану атестації
                  </label>
                </div>
              </div>
            </div>

            {/* Підрозділи */}
            <div className="section">
              <h6>Підрозділи</h6>
              <div className="table-bordered-wrap">
                <table className="subdiv-table">
                  <thead>
                    <tr>
                      <th>Посада *</th>
                      <th>Підв.</th>
                      <th>Одиниця *</th>
                      <th>За сумісн.</th>
                      {isEditing && <th className="actions">Дії</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {employee.departments.map((dept, index) => (
                      <tr key={index} className={dept.selected ? 'selected' : ''}>
                        <td style={{ position: 'relative', minWidth: '370px' }}>
                          {isEditing ? (
                            <div className="position-select-container">
                              <select
                                className={`form-select ${getFieldErrorClass('departments', index)}`}
                                value={dept.staffPositionId}
                                onChange={(e) => {
                                  handleDepartmentChange(index, 'staffPositionId', e.target.value);
                                  if (validationErrors.departments?.[index]?.staffPositionId) {
                                    setValidationErrors(prev => ({
                                      ...prev,
                                      departments: {
                                        ...prev.departments,
                                        [index]: {...prev.departments[index], staffPositionId: undefined}
                                      }
                                    }));
                                  }
                                }}
                                disabled={loading}
                                required
                              >
                                <option value="">Оберіть посаду</option>
                                {availablePositions
                                  .map(position => (
                                    <option key={position.id} value={position.id}>
                                      {position.positionName} ({position.availableUnits} з {position.totalUnits} доступно)
                                    </option>
                                  ))
                                }
                              </select>
                            </div>
                          ) : (
                            <div className="text-value">{dept.positionName || '-'}</div>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={dept.promotion}
                              onChange={(e) => handleDepartmentChange(index, 'promotion', e.target.checked)}
                              disabled={loading}
                            />
                          ) : (
                            <div className="text-value">{dept.promotion ? 'Так' : 'Ні'}</div>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              className={`form-control ${getFieldErrorClass('departments', index)}`}
                              value={dept.unit}
                              onChange={(e) => {
                                handleDepartmentChange(index, 'unit', e.target.value);
                                if (validationErrors.departments?.[index]?.unit) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    departments: {
                                      ...prev.departments,
                                      [index]: {...prev.departments[index], unit: undefined}
                                    }
                                  }));
                                }
                              }}
                              disabled={loading}
                              //required
                            />
              
                          ) : (
                            <div className="text-value">{dept.unit}</div>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={dept.combination}
                              onChange={(e) => handleDepartmentChange(index, 'combination', e.target.checked)}
                              disabled={loading}
                            />
                          ) : (
                            <div className="text-value">{dept.combination ? 'Так' : 'Ні'}</div>
                          )}
                        </td>
                        {isEditing && (
                          <td className="actions">
                            <button
                              className="action-btn"
                              title="Вгору"
                              onClick={() => moveDepartment(index, 'up')}
                              disabled={loading || index === 0}
                              type="button"
                            >
                              <span>↑</span>
                            </button>
                            <button
                              className="action-btn"
                              title="Вниз"
                              onClick={() => moveDepartment(index, 'down')}
                              disabled={loading || index === employee.departments.length - 1}
                              type="button"
                            >
                              <span>↓</span>
                            </button>
                            <button
                              className="action-btn"
                              title="Видалити"
                              onClick={() => deleteDepartment(index)}
                              disabled={loading || employee.departments.length === 1}
                              type="button"
                            >
                              <span>🗑️</span>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {isEditing && (
                  <div className="table-actions">
                    <a
                      className="add-contact-link"
                      onClick={addDepartment}
                      disabled={loading}
                    >
                      Додати одиницю
                    </a>
                  </div>
                )}
              </div>
            </div>
             {employeeData && (
              <EmployeePublicProfile
                publicProfile={employee.publicProfile}
                isEditing={isEditing}
                onChange={(updates) => {
                  
                   if (updates.bio) console.log('bio update length:', updates.bio.length);
                  setEmployee(prev => ({
                  ...prev,
                  publicProfile: { ...prev.publicProfile, ...updates }
                }))}}
              />
            )}
          </div>

          <div className="modal-footer justify-content-between">
            <div>
              {!isEditing && employeeData && employeeData.status !== 'dismissed' && (
                <button 
                  className="btn btn-link-danger" 
                  onClick={handleDismiss}
                  disabled={loading}
                  type="button"
                >
                  Звільнити
                </button>
              )}
              {!isEditing && employeeData && employeeData.status === 'dismissed' && (
                <button 
                  className="btn btn-outline-success" 
                  onClick={handleRestore}
                  disabled={loading}
                  type="button"
                >
                  Поновити
                </button>
              )}
            </div>
            <div>
              <button 
                className="btn btn-ghost-primary me-2" 
                onClick={handleClose}
                disabled={loading}
                type="button"
              >
                {isEditing ? 'Скасувати' : 'Закрити'}
              </button>
              {isEditing && (
                <button 
                  className="btn btn-primary" 
                  onClick={handleSave}
                  disabled={loading}
                  type="button"
                >
                  {loading ? 'Збереження...' : 'Зберегти'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeModal;